// Plain-English rendering of a custom alarm (custom + conditional alarms,
// phase 5). One pure function turns a `custom` block plus its reserved
// threshold keys into the sentence the builder shows while you author
// ("always-visible review sentence"), the name the builder prefills, and the
// line the configured-alarms list and the detail page render for a custom def.
//
//     House battery voltage goes above 26 V for 5 s (clears below 25.5 V)
//
// Three rules keep it honest:
//   - It states what the COMPILER will do, not what the form holds. A missing
//     staleness window renders the compiler's default ("for 5 min"), because
//     that is the alarm the boat will actually run.
//   - It omits what does not bite: a zero delay and a clear point equal to the
//     set point are both no-ops, so neither earns a clause.
//   - Numbers arrive in VERBATIM STORE UNITS (plan D6) — the unit string comes
//     from the signal catalog — and are PRINTED in the account's units when the
//     caller names a category (ADR 0189's scoped presentation exception, below).
//
// ADR 0189, and what it did NOT change. Plan D6 made every number here the
// stored one because the alarm domain cannot afford a conversion it did not
// intend: storage and the wire are the boat's units, and the compiler reads
// them. That still holds. What ADR 0189 adds is one scoped exception at the
// PRESENTATION boundary — a depth alarm reads "1.6 ftm" to an account that asked
// for fathoms — carried by two optional arguments (`category`, `units`) that a
// caller passes only when it knows the quantity. With neither, nothing converts.
// Knots, percent and volts that nothing converts are still rounded for display
// by `roundedReading` in `@neomaris/protocol` (TOM-860).
//
// The exception's three limits:
//   - STORAGE AND WIRE ARE UNTOUCHED. Nothing converted here is ever written
//     back. The edit path converts separately and preserves an untouched stored
//     number BIT-IDENTICALLY, because a rounded re-save would change the boat's
//     `defsDigest` and resync the whole ruleset.
//   - COMPARISONS STAY IN STORE UNITS. `wouldFireNow` and friends judge the raw
//     numbers; conversion happens at format time and nowhere earlier.
//   - CONDITION CLAUSES DO NOT CONVERT — see {@link ConditionsOptions.unitFor}.
//
// Phase 6 adds the conditions clause ("— only while engine RPM is above 500"):
// {@link conditionsClause} composes it from the clause list, and
// {@link SentenceOptions.conditionClause} appends it, so both surfaces
// punctuate the same way.

import { formatDurationSeconds, isSecondsUnit, type UnitSettings } from '@neomaris/protocol';

import { convertQuantity, formatQuantity, roundedReading, type UnitCategory } from './displayUnits';
import { signalLabel, type TriggerClass } from './signalCatalog';

/** The custom spec as it arrives from the wire (`definition.custom`). */
export interface CustomSentenceSpec {
  readonly triggerClass: TriggerClass;
  readonly path?: string;
}

/**
 * A threshold block, read defensively. Accepts both a freshly built
 * {@link import('./customAlarmForm').CustomThresholdBlock} and a STORED block
 * off the wire, whose values are `unknown` until proven numeric.
 */
export type SentenceThresholds = Readonly<Record<string, unknown>> | null | undefined;

export interface SentenceOptions {
  /** Human name for the signal — `signalLabel(path)` at the call site. */
  readonly label: string;
  /** The signal's declared STORE unit — what the stored numbers are in. */
  readonly unit?: string | null;
  /**
   * What the numbers MEASURE, when the caller knows (`categoryFor(path, unit)`).
   * With it and {@link SentenceOptions.units} the set point and clear point
   * print in the account's units; without either they print stored (ADR 0189).
   */
  readonly category?: UnitCategory;
  /** The account's unit settings (`useUnits()`), needed for any conversion. */
  readonly units?: UnitSettings;
  /**
   * Phase 6: an already-composed conditions clause ("only while engine RPM is
   * above 500"), appended after an em dash.
   */
  readonly conditionClause?: string;
  /**
   * Print set and clear points as stored, without `roundedReading`'s rounding
   * (TOM-860). Titles are written and matched in this spelling, so an
   * alarm named "… goes above 10.29 knots" keeps its name.
   */
  readonly exact?: boolean;
}

/**
 * The staleness window a `DataUnavailable` alarm gets when none is authored —
 * mirrors the compiler's `DEFAULT_CUSTOM_STALE_MS` (5 min). Rendered rather
 * than hidden: the sentence describes the alarm the boat will run.
 */
const DEFAULT_STALE_MS = 300_000;

/** The API's `title` bound. */
const MAX_TITLE_LENGTH = 200;

function finite(thresholds: SentenceThresholds, key: string): number | undefined {
  const raw = thresholds?.[key];
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined;
}

/** A number for prose: no exponent, no trailing zeros, at most 3 decimals. */
function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return '';
  return String(Math.round(value * 1000) / 1000);
}

/**
 * One stored number restated in the account's units, or `undefined` when
 * nothing moves — THE gate every converting formatter in the alarm surfaces
 * goes through (ADR 0189).
 *
 * Three ways it declines, and each matters:
 *   - the caller named no category, or holds no settings — it does not know
 *     what the number measures, and guessing is how a set point gets divided by
 *     33.86;
 *   - the value is not finite — the legacy path renders those as an empty
 *     string, and `NaN.toFixed(1)` would print the word "NaN" instead;
 *   - the account's unit for that category IS the store unit, so the number
 *     would not move.
 *
 * That last test is made by COMPARING LABELS — what `formatQuantity` calls the
 * result with the category against what it calls it without. Equal labels mean
 * the store-unit gate inside `displayUnits` chose the identity rule, so there is
 * nothing to convert; the caller then renders exactly what it rendered before
 * this PR, byte for byte. Declining rather than returning an identity result is
 * the whole regression guarantee: `formatQuantity` would re-decide the
 * precision ("3 m" becoming "3.0 m") on a value nobody asked to convert.
 */
export function convertedQuantity(
  value: number,
  storeUnit: string | null | undefined,
  category: UnitCategory | undefined,
  units: UnitSettings | undefined
): { text: string; unit: string } | undefined {
  if (category === undefined || units === undefined) return undefined;
  if (!Number.isFinite(value)) return undefined;
  const converted = formatQuantity(value, storeUnit, category, units);
  if (converted.unit === formatQuantity(value, storeUnit, undefined, units).unit) return undefined;
  return converted;
}

/**
 * A value with its unit. The space is dropped for units that read as a suffix
 * ("50%", "12°") and kept for everything else ("26 V", "40 kPa").
 *
 * `unit` is the number's STORE unit. Name `category` and pass `units` and the
 * pair converts — "3 m" becomes "9.8 ft", "95°C" becomes "203.0°F" — taking the
 * precision that goes with the unit actually shown (`displayUnits`), not this
 * module's stored-number rounding. The spacing rule then applies to the
 * CONVERTED label, so a Fahrenheit reading is glued to its glyph exactly as a
 * Celsius one is. Omit either argument and nothing converts. A number in knots,
 * percent or volts that nothing converts is rounded by `roundedReading`, unless
 * `exact` asks for the stored number (titles, TOM-860).
 *
 * `minDecimals` prints at least that many decimals. {@link distinctDecimals}
 * picks it, so two different numbers on one line never read the same.
 */
export function formatThreshold(
  value: number,
  unit?: string | null,
  category?: UnitCategory,
  units?: UnitSettings,
  opts: ThresholdFormat = {}
): string {
  // A duration reads in days, hours, minutes and seconds (TOM-742).
  if (isSecondsUnit(unit)) return formatDurationSeconds(value);
  const converted = convertedQuantity(value, unit, category, units);
  let text: string;
  let shown = value;
  if (converted !== undefined) {
    text = converted.text;
    shown = convertQuantity(value, unit, category, units as UnitSettings).value;
  } else {
    text = (opts.exact === true ? undefined : roundedReading(value, unit)) ?? formatNumber(value);
  }
  if (opts.minDecimals !== undefined && decimalsIn(text) < opts.minDecimals) {
    text = shown.toFixed(opts.minDecimals);
  }
  const u = (converted?.unit ?? unit ?? '').trim();
  if (u.length === 0) return text;
  if (u === '%' || u.startsWith('°')) return `${text}${u}`;
  return `${text} ${u}`;
}

/** How {@link formatThreshold} prints a number beyond its unit's own rule. */
export interface ThresholdFormat {
  /** The stored number, not rounded (titles, TOM-860). */
  readonly exact?: boolean;
  /** At least this many decimals (TOM-860). */
  readonly minDecimals?: number;
}

function decimalsIn(text: string): number {
  const dot = text.indexOf('.');
  return dot === -1 ? 0 : text.length - dot - 1;
}

/**
 * For numbers that share one line, the decimals each must print so that two
 * different numbers never read the same (TOM-860). Rounding alone can print a
 * set point of 10.29 kn and a clear point of 10.1 kn both as "10 knots", or a
 * reading of 11.76 V beside a set point of 11.84 V both as "11.8 V".
 *
 * Returns a lookup by value. Only the numbers whose rounded text collides get
 * an answer: the fewest decimals, at most 3, that tell that group apart. Every
 * other number, and any number not in `values`, gets `undefined` and keeps its
 * unit's own rounding, so a 15 kn reading beside that pair still reads "15".
 */
export function distinctDecimals(
  values: readonly (number | null | undefined)[],
  unit?: string | null,
  category?: UnitCategory,
  units?: UnitSettings
): (value: number | null | undefined) => number | undefined {
  const distinct = [
    ...new Set(values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v))),
  ];
  const text = (v: number, minDecimals?: number): string =>
    formatThreshold(v, unit, category, units, { minDecimals });
  const groups = new Map<string, number[]>();
  for (const v of distinct) groups.set(text(v), [...(groups.get(text(v)) ?? []), v]);
  const decimals = new Map<number, number>();
  for (const group of groups.values()) {
    if (group.length < 2) continue;
    const d = [1, 2].find((n) => new Set(group.map((v) => text(v, n))).size === group.length) ?? 3;
    for (const v of group) decimals.set(v, d);
  }
  return (value) => (typeof value === 'number' ? decimals.get(value) : undefined);
}

/**
 * A duration in the largest unit that stays exact and readable: seconds under a
 * minute, minutes under an hour, then hours — with a remainder only when there
 * is one ("5 s", "5 min", "1 min 30 s", "1 h 30 min").
 */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0 s';
  const seconds = ms / 1000;
  if (seconds < 60) return `${formatNumber(seconds)} s`;
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const rest = seconds - minutes * 60;
    return rest === 0 ? `${minutes} min` : `${minutes} min ${formatNumber(rest)} s`;
  }
  const hours = Math.floor(seconds / 3600);
  const restMinutes = Math.round((seconds - hours * 3600) / 60);
  return restMinutes === 0 ? `${hours} h` : `${hours} h ${restMinutes} min`;
}

/** " for 5 s", or nothing when the delay is absent or zero. */
function delayClause(ms: number | undefined): string {
  return ms !== undefined && ms > 0 ? ` for ${formatDuration(ms)}` : '';
}

/**
 * The parenthetical that says how the alarm goes away: the clear point when it
 * differs from the set point (an equal one is no hysteresis at all), plus the
 * clear delay when there is one. Empty when neither bites.
 */
function clearsClause(
  triggerClass: 'High' | 'Low',
  setpoint: number | undefined,
  clearpoint: number | undefined,
  clearDelayMs: number | undefined,
  unit?: string | null,
  category?: UnitCategory,
  units?: UnitSettings,
  format: ThresholdFormat = {}
): string {
  // The set/clear COMPARISON is made on the stored numbers, before either is
  // converted — an equal pair is no hysteresis whatever units it is read in.
  const showClearpoint = clearpoint !== undefined && clearpoint !== setpoint;
  const delay = delayClause(clearDelayMs);
  if (showClearpoint) {
    const direction = triggerClass === 'High' ? 'below' : 'above';
    const point = formatThreshold(clearpoint, unit, category, units, format);
    return ` (clears ${direction} ${point}${delay})`;
  }
  return delay.length > 0 ? ` (clears${delay})` : '';
}

/**
 * The plain-English sentence for a custom alarm. Never blank: an unrecognised
 * trigger class (a definition written by a newer build) degrades to a neutral
 * phrase rather than inventing a threshold.
 */
export function customAlarmSentence(
  custom: CustomSentenceSpec,
  thresholds: SentenceThresholds,
  options: SentenceOptions
): string {
  const label = options.label.trim().length > 0 ? options.label.trim() : 'This signal';
  const unit = options.unit;
  const category = options.category;
  const units = options.units;
  const exact = options.exact === true;
  const setDelayMs = finite(thresholds, 'setDelayMs');
  let sentence: string;

  switch (custom.triggerClass) {
    case 'High':
    case 'Low': {
      const setpoint = finite(thresholds, 'setpoint');
      const clearpoint = finite(thresholds, 'clearpoint');
      const verb = custom.triggerClass === 'High' ? 'goes above' : 'drops below';
      // A set and clear point that round to the same text get a decimal more.
      const decimalsFor = distinctDecimals([setpoint, clearpoint], unit, category, units);
      const target =
        setpoint === undefined
          ? 'its set point'
          : formatThreshold(setpoint, unit, category, units, {
              exact,
              minDecimals: exact ? undefined : decimalsFor(setpoint),
            });
      sentence =
        `${label} ${verb} ${target}` +
        delayClause(setDelayMs) +
        clearsClause(
          custom.triggerClass,
          setpoint,
          clearpoint,
          finite(thresholds, 'clearDelayMs'),
          unit,
          category,
          units,
          { exact, minDecimals: exact ? undefined : decimalsFor(clearpoint) }
        );
      break;
    }
    case 'On':
    case 'Off': {
      const verb = custom.triggerClass === 'On' ? 'turns on' : 'turns off';
      const clearDelay = delayClause(finite(thresholds, 'clearDelayMs'));
      sentence =
        `${label} ${verb}` +
        delayClause(setDelayMs) +
        (clearDelay.length > 0 ? ` (clears${clearDelay})` : '');
      break;
    }
    case 'DataUnavailable': {
      const staleMs = finite(thresholds, 'staleMs') ?? DEFAULT_STALE_MS;
      sentence = `${label} stops reporting for ${formatDuration(staleMs)}`;
      break;
    }
    default:
      sentence = `${label} meets its trigger`;
      break;
  }

  const condition = options.conditionClause?.trim();
  return condition !== undefined && condition.length > 0 ? `${sentence} — ${condition}` : sentence;
}

// --- conditions ("only while …") -------------------------------------------

/**
 * One qualifier clause as the wire carries it (`alertConditionClauseSchema`):
 * either the recipes' engine-running preset, or an explicit path condition.
 */
export type ConditionClauseSpec =
  | { readonly preset: 'engine_running'; readonly minRpm?: number }
  | {
      readonly type: 'High' | 'Low' | 'Active' | 'Inactive';
      readonly path: string;
      readonly value?: number;
    };

/** A whole `conditions` block: how the clauses fold, and the clauses. */
export interface ConditionsSpec {
  /** `all` (AND, the default) or `any` (OR). */
  readonly logic?: 'all' | 'any';
  readonly clauses: readonly ConditionClauseSpec[];
  /**
   * How many STORED clauses this build could not read — a clause type written
   * by a newer build, or a corrupt one. Carried rather than forgotten so the
   * prose can say the readable clauses are not the whole gate; see
   * {@link conditionsClause}.
   */
  readonly dropped?: number;
}

export interface ConditionsOptions {
  /**
   * Human name for a clause's path. Defaults to {@link signalLabel}, which
   * derives a name from the path alone — pass a catalog-aware version when the
   * device name is available.
   */
  readonly labelFor?: (path: string) => string;
  /**
   * The declared store unit for a clause's path, printed as-is (plan D6).
   *
   * A condition clause does NOT take ADR 0189's presentation exception, on
   * purpose. Its threshold is rendered on three surfaces — this tail, the
   * read-only conditions block, and the live panel's clause rows — and the
   * panel's expectation text is composed elsewhere (`customAlarmConditions`).
   * Converting here alone would put two units on one clause on one page, which
   * is the mixed-unit failure the exception exists to avoid. Convert all three
   * together or none; today, none.
   */
  readonly unitFor?: (path: string) => string | null | undefined;
  /** Print clause values as stored, without rounding (titles, TOM-860). */
  readonly exact?: boolean;
  /**
   * How many clauses to name before summarising. Defaults to the v1 cap (4), so
   * an authored alarm always reads in full; a tight surface (a list row) can
   * pass a smaller number.
   */
  readonly maxClauses?: number;
}

/** The v1 clause cap — also the default number the sentence names in full. */
const DEFAULT_MAX_CLAUSES = 4;

/** "Engine 1 revolutions is above 500", "shore power is off", "the engine is running". */
function clausePhrase(clause: ConditionClauseSpec, options: ConditionsOptions): string | undefined {
  if ('preset' in clause) {
    const minRpm = clause.minRpm;
    return minRpm !== undefined && Number.isFinite(minRpm)
      ? `the engine is running above ${formatNumber(minRpm)} rpm`
      : 'the engine is running';
  }
  const path = clause.path;
  if (typeof path !== 'string' || path.trim().length === 0) return undefined;
  const label = (options.labelFor ?? ((p: string) => signalLabel(p)))(path);
  const name = label.trim().length > 0 ? label.trim() : 'this signal';
  switch (clause.type) {
    case 'High':
    case 'Low': {
      const value = clause.value;
      if (value === undefined || !Number.isFinite(value)) return undefined;
      const unit = options.unitFor?.(path);
      const direction = clause.type === 'High' ? 'above' : 'below';
      const point = formatThreshold(value, unit, undefined, undefined, { exact: options.exact });
      return `${name} is ${direction} ${point}`;
    }
    case 'Active':
      return `${name} is on`;
    case 'Inactive':
      return `${name} is off`;
    default:
      return undefined;
  }
}

/** "A", "A and B", "A, B and C" — or "or" when the clauses fold with `any`. */
function joinPhrases(parts: readonly string[], conjunction: string): string {
  if (parts.length <= 1) return parts[0] ?? '';
  const head = parts.slice(0, -1).join(', ');
  return `${head} ${conjunction} ${parts[parts.length - 1]}`;
}

/**
 * The plain-English conditions clause — "only while Engine 1 revolutions is
 * above 500 and shore power is off" — ready to hand to
 * {@link SentenceOptions.conditionClause}. Empty string when there is nothing
 * honest to say (no clauses, or none this build can read).
 *
 * `all` joins with "and", `any` with "or". Past `maxClauses` the tail is
 * summarised ("… and 2 more conditions") rather than truncated mid-phrase: a
 * clipped clause would read as a DIFFERENT alarm, which is the one thing a
 * safety sentence must never do.
 *
 * A clause this build cannot read (`ConditionsSpec.dropped`, or a clause whose
 * phrase does not compose) is NAMED — "and a condition this app can’t display
 * yet" — never silently omitted. Without that, the readable clauses would read
 * as the whole gate: "only while A or B" is a definitive claim, and a hidden
 * third clause makes it false.
 */
export function conditionsClause(
  conditions: ConditionsSpec | null | undefined,
  options: ConditionsOptions = {}
): string {
  const clauses = conditions?.clauses;
  if (clauses === undefined || clauses.length === 0) return '';
  const raw = conditions?.dropped;
  let unreadable = typeof raw === 'number' && raw > 0 ? Math.trunc(raw) : 0;
  const phrases: string[] = [];
  for (const clause of clauses) {
    const phrase = clausePhrase(clause, options);
    if (phrase === undefined) unreadable += 1;
    else phrases.push(phrase);
  }
  if (phrases.length === 0) return '';

  const conjunction = conditions?.logic === 'any' ? 'or' : 'and';
  const max = Math.max(1, Math.trunc(options.maxClauses ?? DEFAULT_MAX_CLAUSES));
  const hiddenPhrase =
    unreadable === 1
      ? 'a condition this app can’t display yet'
      : `${unreadable} conditions this app can’t display yet`;
  const parts = unreadable > 0 ? [...phrases, hiddenPhrase] : phrases;
  if (parts.length <= max) return `only while ${joinPhrases(parts, conjunction)}`;

  const shown = phrases.slice(0, max);
  const hidden = phrases.length - shown.length + unreadable;
  const more = `${hidden} more condition${hidden === 1 ? '' : 's'}`;
  return `only while ${joinPhrases([...shown, more], conjunction)}`;
}

/**
 * Read a stored `conditions` block defensively — the same discipline as
 * {@link SentenceThresholds}. Wire data written by a newer build (or a corrupt
 * row) must degrade, never throw: unreadable clauses are dropped, and a block
 * with nothing readable left comes back `undefined` so the caller renders no
 * conditions clause rather than an empty "only while".
 *
 * Dropped is not forgotten. The count comes back as {@link
 * ConditionsSpec.dropped} so the prose can disclose that clauses exist which
 * this build cannot show — the survivors are not the whole gate.
 */
export function readConditions(raw: unknown): ConditionsSpec | undefined {
  if (raw === null || typeof raw !== 'object') return undefined;
  const { logic, clauses } = raw as { logic?: unknown; clauses?: unknown };
  if (!Array.isArray(clauses)) return undefined;
  const read: ConditionClauseSpec[] = [];
  let dropped = 0;
  for (const clause of clauses as unknown[]) {
    if (clause === null || typeof clause !== 'object') {
      dropped += 1;
      continue;
    }
    const c = clause as {
      preset?: unknown;
      minRpm?: unknown;
      type?: unknown;
      path?: unknown;
      value?: unknown;
    };
    if (c.preset === 'engine_running') {
      read.push(
        typeof c.minRpm === 'number' && Number.isFinite(c.minRpm)
          ? { preset: 'engine_running', minRpm: c.minRpm }
          : { preset: 'engine_running' }
      );
      continue;
    }
    const path = typeof c.path === 'string' ? c.path.trim() : '';
    if (path.length === 0) {
      dropped += 1;
      continue;
    }
    if (c.type === 'High' || c.type === 'Low') {
      if (typeof c.value !== 'number' || !Number.isFinite(c.value)) {
        dropped += 1;
        continue;
      }
      read.push({ type: c.type, path, value: c.value });
      continue;
    }
    if (c.type === 'Active' || c.type === 'Inactive') {
      read.push({ type: c.type, path });
      continue;
    }
    dropped += 1;
  }
  if (read.length === 0) return undefined;
  return {
    logic: logic === 'any' ? 'any' : 'all',
    clauses: read,
    ...(dropped > 0 ? { dropped } : {}),
  };
}

/**
 * The name the builder prefills: the sentence, cut to the API's 200-character
 * bound. The cut keeps whole information where it can — it trims trailing
 * whitespace and marks the truncation with an ellipsis so a clipped name reads
 * as clipped rather than as a different alarm.
 *
 * The numbers are the stored ones (`exact`), not the rounded display: a title
 * is stored, and the Alarms page matches it against this spelling (TOM-860).
 */
export function defaultTitle(
  custom: CustomSentenceSpec,
  thresholds: SentenceThresholds,
  options: SentenceOptions
): string {
  const sentence = customAlarmSentence(custom, thresholds, { ...options, exact: true });
  if (sentence.length <= MAX_TITLE_LENGTH) return sentence;
  return `${sentence.slice(0, MAX_TITLE_LENGTH - 1).trimEnd()}…`;
}
